import { supabaseClient } from "@/lib/supabase";
import type { Json } from "@/types/database/database";
import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import { toast } from "@patient-watch/ui/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface FormTranslationRowT {
    locale: string;
    form_name: string | null;
    form_description: string | null;
    form_instructions: string | null;
    form_caption: string | null;
}

export interface PageTranslationRowT {
    page_id: string;
    locale: string;
    page_label: string | null;
    page_description: string | null;
}

export interface BlockTranslationRowT {
    block_id: string;
    locale: string;
    content: string | null;
    caption: string | null;
}

export interface InputTranslationRowT {
    input_id: string;
    locale: string;
    input_label: string | null;
    input_caption: string | null;
    options: string[] | null;
    scale_options: Json | null;
}

export interface FormTranslationsT {
    forms: FormTranslationRowT[];
    pages: PageTranslationRowT[];
    inputs: InputTranslationRowT[];
    blocks: BlockTranslationRowT[];
}

/** True once any locale has translated text for this form. Saved rows can be blank after someone clears them. */
export function hasFormTranslations(data: FormTranslationsT | undefined): boolean {
    if (!data) return false;
    const filled = (value: unknown) => (typeof value === "string" ? value.trim() !== "" : value != null);
    return (
        data.forms.some((row) =>
            [row.form_name, row.form_caption, row.form_description, row.form_instructions].some(filled)
        ) ||
        data.pages.some((row) => [row.page_label, row.page_description].some(filled)) ||
        data.blocks.some((row) => [row.content, row.caption].some(filled)) ||
        data.inputs.some((row) => [row.input_label, row.input_caption].some(filled))
    );
}

const translationKeys = {
    byForm: (formId: string) => ["form-builder", formId, "translations"] as const,
};

/** Every locale's translations for a form, its pages and its questions. */
async function selectFormTranslations(form: FormBuilderData): Promise<FormTranslationsT> {
    const pageIds = form.pages.map((page) => page.page_id);
    const inputIds = form.pages.flatMap((page) => page.inputs.map((input) => input.input_id));
    const blockIds = form.pages.flatMap((page) => page.blocks.map((block) => block.block_id));

    const [forms, pages, inputs, blocks] = await Promise.all([
        supabaseClient
            .from("form_translations")
            .select("locale, form_name, form_description, form_instructions, form_caption")
            .eq("form_id", form.form_id),
        pageIds.length
            ? supabaseClient
                  .from("page_translations")
                  .select("page_id, locale, page_label, page_description")
                  .in("page_id", pageIds)
            : Promise.resolve({ data: [], error: null }),
        inputIds.length
            ? supabaseClient
                  .from("input_translations")
                  .select("input_id, locale, input_label, input_caption, options, scale_options")
                  .in("input_id", inputIds)
            : Promise.resolve({ data: [], error: null }),
        blockIds.length
            ? supabaseClient
                  .from("block_translations")
                  .select("block_id, locale, content, caption")
                  .in("block_id", blockIds)
            : Promise.resolve({ data: [], error: null }),
    ]);
    for (const result of [forms, pages, inputs, blocks]) if (result.error) throw result.error;

    return {
        forms: (forms.data ?? []) as FormTranslationRowT[],
        pages: (pages.data ?? []) as PageTranslationRowT[],
        inputs: (inputs.data ?? []) as InputTranslationRowT[],
        blocks: (blocks.data ?? []) as BlockTranslationRowT[],
    };
}

export function useFormTranslations(form: FormBuilderData, { enabled = true }: { enabled?: boolean } = {}) {
    return useQuery({
        queryKey: translationKeys.byForm(form.form_id),
        queryFn: () => selectFormTranslations(form),
        enabled,
    });
}

export type TranslationUpsertT =
    | { table: "form_translations"; row: Partial<FormTranslationRowT> & { form_id: string; locale: string } }
    | { table: "page_translations"; row: Partial<PageTranslationRowT> & { page_id: string; locale: string } }
    | { table: "block_translations"; row: Partial<BlockTranslationRowT> & { block_id: string; locale: string } }
    | { table: "input_translations"; row: Partial<InputTranslationRowT> & { input_id: string; locale: string } };

const conflictTarget = {
    form_translations: "form_id,locale",
    page_translations: "page_id,locale",
    input_translations: "input_id,locale",
    block_translations: "block_id,locale",
} as const;

/** Saves one translated field. Other fields on the same row are left as they are. */
export function useUpsertTranslation(formId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async ({ table, row }: TranslationUpsertT) => {
            const { error } = await (supabaseClient.from(table) as any).upsert(
                { ...row, updated_at: new Date().toISOString() },
                { onConflict: conflictTarget[table] }
            );
            if (error) throw error;
        },
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: translationKeys.byForm(formId) }),
        onError: () => toast.error("Could not save the translation"),
    });
}
